import AppContext from 'App/AppContext';
import { EntityTypes } from 'models/entity';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { apiGetTasksForAuditEntity } from 'services/Api/taskService';
import { apiRequest } from 'services/Auth/authConfig';

export const auditTasksPivotKey = 'auditTasks';

export interface IAuditTaskResourceTab {
  selectedPivot: string;
  setSelectedPivot: (key: string) => void;
  auditTaskCount: number;
  initialTaskId: number | undefined;
  initialInternalAuditId: number | undefined;
  onAvailabilityChange: (taskCount: number) => void;
}

/**
 * The audit-task tab of an entity scene's resource pane opens ITSELF. Once per entity the hook asks for
 * the caller's open execution work on it - the same answer that decides whether the task may still be
 * edited - and when there is any, or the URL names a task, it selects the tab and expands the pane. It
 * asks on its own because ResourcePanel mounts nothing while collapsed, so the pane's own loader can
 * never be the trigger. Selecting another tab or collapsing the pane afterwards is respected until the
 * user moves to another entity. An answer of 0 while the tab is selected falls back to the scene's
 * default tab, because the pivot item disappears with it.
 *
 * `auditTaskId` and `internalAuditId` in the URL stay a preference: they pick the task when the caller
 * has several on the entity, and they are what makes a colleague's task readable for a team member. The
 * pane resolves both; this hook only makes sure the tab is open to show them.
 */
export const useAuditTaskResourceTab = (
  entityType: EntityTypes,
  entityId: number | undefined,
  defaultPivot: string,
): IAuditTaskResourceTab => {
  const appContext = useContext(AppContext);
  const { search } = useLocation();
  const [selectedPivot, setSelectedPivot] = useState<string>(defaultPivot);
  const [auditTaskCount, setAuditTaskCount] = useState<number>(0);
  const query = new URLSearchParams(search);
  const initialTaskId = Number(query.get('auditTaskId')) || undefined;
  const initialInternalAuditId = Number(query.get('internalAuditId')) || undefined;

  // The probe and the callback handed to the pane read the current render through refs: the callback
  // is in the pane's loader deps, so a new identity would reload the pane on every render.
  const appContextRef = useRef(appContext);
  const selectedPivotRef = useRef(selectedPivot);
  const defaultPivotRef = useRef(defaultPivot);
  const requestedTaskRef = useRef({ initialTaskId, initialInternalAuditId });
  const entityKeyRef = useRef<string>('');
  appContextRef.current = appContext;
  selectedPivotRef.current = selectedPivot;
  defaultPivotRef.current = defaultPivot;
  requestedTaskRef.current = { initialTaskId, initialInternalAuditId };

  const leaveTab = useCallback(() => {
    if (selectedPivotRef.current === auditTasksPivotKey) setSelectedPivot(defaultPivotRef.current);
  }, []);

  const openTab = useCallback(() => {
    setSelectedPivot(auditTasksPivotKey);
    // on the phone layout the pane is an overlay over the very item the auditee came to read:
    // select the tab, do not force the pane open
    if (!appContextRef.current.isMobileView) appContextRef.current.setShowResourcePanel(true);
  }, []);

  const onAvailabilityChange = useCallback(
    (taskCount: number) => {
      setAuditTaskCount(taskCount);
      if (taskCount === 0) leaveTab();
    },
    [leaveTab],
  );

  useEffect(() => {
    const entityKey = `${entityType}:${entityId ?? 0}`;
    entityKeyRef.current = entityKey;
    if (!entityId) return;

    const probe = async () => {
      try {
        const accessToken = await appContextRef.current.getAccessToken(apiRequest.scopes);
        const taskIds = await apiGetTasksForAuditEntity(entityType, entityId, accessToken);
        // the user moved on while this was in flight
        if (entityKeyRef.current !== entityKey) return;

        setAuditTaskCount(taskIds.length);
        const requested = requestedTaskRef.current;
        // a named task is an explicit request: a colleague's task is not in MY list, the pane resolves it
        if (taskIds.length > 0 || (requested.initialTaskId && requested.initialInternalAuditId)) {
          openTab();
        } else {
          leaveTab();
        }
      } catch (err) {
        if (entityKeyRef.current !== entityKey) return;

        appContextRef.current.setError(err);
        onAvailabilityChange(0);
      }
    };

    void probe();
  }, [entityType, entityId, openTab, leaveTab, onAvailabilityChange]);

  return {
    selectedPivot,
    setSelectedPivot,
    auditTaskCount,
    initialTaskId,
    initialInternalAuditId,
    onAvailabilityChange,
  };
};
